import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "services/config/url";
import type { AxiosError, AxiosRequestConfig } from "axios";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { axiosInterceptors } from "services/config/axiosInterceptors";
import {
  IFCM,
  IFCMDelete,
  ILoginProps,
  IOtpProps,
  IProfileProps,
  IProfileUpdateProps,
  IResetPassProps,
  IServiceAssign,
  IServiceEdit,
  ISignupProps,
  ITrackerAssign,
  ITrackerUpdate,
  IVerifyProps,
} from "types/servicesTypes/types";
import { TrackerResponse } from "utils/v2/API/TrackerResponse";

const axiosBaseQuery =
  (
    {
      baseUrl,
    }: {
      baseUrl: string;
    } = { baseUrl: "https://stg.radlenk.com/api/v1" },
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params }) => {
    try {
      const result = await axiosInterceptors({
        url: baseUrl + url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError as AxiosError;

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
const baseQuery = axiosBaseQuery({
  baseUrl: baseUrl ? baseUrl : "https://stg.radlenk.com/api/v1",
});

const fetchBaseQueries = retry(baseQuery, { maxRetries: 0 });

export const IlenkradApi = createApi({
  reducerPath: "ilenkard",
  baseQuery: fetchBaseQueries,
  tagTypes: ["ilenkrad"],
  endpoints: (builder) => ({
    authLogin: builder.mutation<void, ILoginProps>({
      query: (data) => ({
        url: "/auth/login",
        method: "POST",
        data: data,
      }),

      invalidatesTags: ["ilenkrad"],
    }),
    authSignup: builder.mutation<void, ISignupProps>({
      query: (data) => ({
        url: "/auth/signup",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    authOtpSend: builder.mutation<void, IVerifyProps>({
      query: (data) => ({
        url: "/auth/otp/send",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    authOtpVerify: builder.mutation<void, IOtpProps>({
      query: (data) => ({
        url: "/auth/otp/verify",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    authResetPassword: builder.mutation<void, IResetPassProps>({
      query: (data) => ({
        url: "/auth/otp/reset-password",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    profileUpdate: builder.query<void, IProfileUpdateProps>({
      query: (data) => ({
        url: "/profile",
        method: "PUT",
        data: data,
      }),
      providesTags: ["ilenkrad"],
    }),
    profileUpdatePassword: builder.mutation<any, any>({
      query: (data) => ({
        url: "/auth/change-password",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),

    profile: builder.query<IProfileProps, void>({
      query: () => ({
        url: "/profile",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),

    trackerIndex: builder.query<any, void>({
      query: () => ({
        url: "/client/trackers",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    informationIndex: builder.query<any, void>({
      query: () => ({
        url: "/client/trackers/information",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    trackerUnAssign: builder.mutation<void, any>({
      query: (id) => ({
        url: `/client/trackers/${id}/unassign`,
        method: "POST",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    trackerAssign: builder.mutation<void, ITrackerAssign>({
      query: (data) => ({
        url: "/client/trackers/assign",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    trackerBeforeUpdate: builder.query<TrackerResponse, string>({
      query: (imei) => ({
        url: `/client/trackers/${imei}`,
        method: "GET",
      }),
      // providesTags: ["ilenkrad"],
    }),
    trackerInformation: builder.query<any, any>({
      query: (imei) => ({
        url: `/client/trackers/information/${imei}`,
        method: "GET",
      }),
    }),
    trackerUpdate: builder.mutation<void, ITrackerUpdate>({
      query: ({ imei, ...rest }) => ({
        url: `/client/trackers/${imei}`,
        method: "PUT",
        data: rest,
      }),
      // invalidatesTags: ["ilenkrad"],
    }),
    trackerLocationLive: builder.query<any, void>({
      query: () => ({
        url: "/client/geo-location/live",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    singleGeoLocation: builder.query<any, string>({
      query: (imei) => ({
        url: `/client/geo-location/live/${imei}`,
        method: "GET",
      }),
      // providesTags: ["ilenkrad"],
    }),
    servicesIndex: builder.query<any, void>({
      query: () => ({
        url: "/client/reminders",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    servicesAssign: builder.mutation<void, IServiceAssign>({
      query: (data) => ({
        url: "/client/reminders",
        data: data,
        method: "POST",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    servicesDelete: builder.mutation<void, any>({
      query: (id) => ({
        url: `/client/reminders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    servicesEdit: builder.mutation<void, IServiceEdit>({
      query: ({ id, ...rest }) => ({
        url: `/client/reminders/${id}`,
        method: "PUT",
        data: rest,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    geoLocationLive: builder.query<any, void>({
      query: () => ({
        url: "/client/geo-location/live",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    geoFenceIndex: builder.query<any, void>({
      query: () => ({
        url: "/client/geo-fences",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    geoFenceShow: builder.query<any, void>({
      query: (id) => ({
        url: `/client/geo-fences/${id}`,
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    geoFenceDelete: builder.mutation<void, any>({
      query: (id) => ({
        url: `/client/geo-fences/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    geoFenceEdit: builder.mutation<void, any>({
      query: ({ id, ...rest }) => ({
        url: `/client/geo-fences/${id}`,
        method: "PUT",
        data: rest,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    geoFenceAssign: builder.mutation<void, any>({
      query: (data) => ({
        url: `/client/geo-fences`,
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    historyReport: builder.mutation<void, any>({
      query: (data) => ({
        url: "/client/geo-location/history-report",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    historyReportFleet: builder.mutation<void, any>({
      query: (data) => ({
        url: "/client/geo-location/fleet-heartbeat-report",
        method: "POST",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    chatIndex: builder.query<any, void>({
      query: () => ({
        url: "/client/chats",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    messageChatIndex: builder.query<any, void>({
      query: (id) => ({
        url: `/client/chats/${id}/messages`,
        method: "GET",
      }),
      // providesTags: ["ilenkrad"],
    }),
    messageChatIndexMutate: builder.mutation<any, any>({
      query: (id) => ({
        url: `/client/chats/${id}/messages`,
        method: "GET",
      }),
    }),
    messageChatBefore: builder.mutation<any, any>({
      query: ({ id, ...rest }) => ({
        url: `/client/chats/${id}/messages?before_message_id=${rest.before_message_id}`,
        method: "GET",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    messageChatFilter: builder.mutation<any, any>({
      query: ({ id, ...rest }) => ({
        url: `/client/chats/${id}/messages?from_date_time=${rest.from_date_time}&message_type=${rest.message_type}`,
        method: "GET",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    preAssignTracker: builder.mutation<void, any>({
      query: (imei) => ({
        url: "/client/trackers/preassign",
        method: "POST",
        data: imei,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    sendMessageChatIndex: builder.mutation<void, any>({
      query: ({ id, ...rest }) => ({
        url: `/client/chats/${id}/messages`,
        method: "POST",
        data: rest,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    onlineCommand: builder.mutation<void, any>({
      query: (data) => ({
        url: "/client/online-command",
        method: "POST",
        data: data,
      }),
      // invalidatesTags: ["ilenkrad"],
    }),
    sendSyncOnlineCommand: builder.mutation<any, any>({
      query: (data) => ({
        url: "/client/online-command/sync",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    onlineCommandCheck: builder.mutation<void, any>({
      query: (data) => ({
        url: `/client/online-command/${data}`,
        method: "GET",
      }),
      // invalidatesTags: ["ilenkrad"],
    }),
    readAllMessage: builder.mutation<void, any>({
      query: () => ({
        url: "/client/chats/read-all",
        method: "POST",
        data: "",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    FcmDevice: builder.mutation<void, IFCM>({
      query: (data) => ({
        url: "/client/fcm-devices",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    FcmDeviceDelete: builder.mutation<void, IFCMDelete>({
      query: (data) => ({
        url: `/client/fcm-devices/${data.token_md5}`,
        method: "DELETE",
        data: data.body,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    sessionsList: builder.query<any, void>({
      query: () => ({
        url: "/auth/sessions",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    sessionsDeleteAll: builder.mutation<void, any>({
      query: (data) => ({
        url: "/auth/sessions",
        method: "DELETE",
        data: "",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    sessionsDelete: builder.mutation<void, any>({
      query: (id) => ({
        url: `/auth/sessions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    sessionsTime: builder.mutation<void, any>({
      query: (data) => ({
        url: "/auth/sessions/time",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    reportLocation: builder.mutation<void, any>({
      query: (data) => ({
        url: "/client/geo-location/geo-fence-report",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    renewals: builder.query<any, void>({
      query: () => ({
        url: "/client/renewals",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    renewalPackages: builder.query<any, void>({
      query: () => ({
        url: "/client/renewals/packages",
        method: "GET",
      }),
      providesTags: ["ilenkrad"],
    }),
    createRenewals: builder.mutation<void, any>({
      query: (data) => ({
        url: "/client/renewals",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    getRenewal: builder.query<void, any>({
      query: (id) => ({
        url: `/client/renewals/${id}`,
        method: "GET",
      }),
    }),
    getEligibleWarranties: builder.query<void, any>({
      query: (data) => ({
        url: `/client/renewals/warranty`,
        method: "POST",
        data: data,
      }),
    }),
    payment: builder.mutation<void, any>({
      query: (data) => ({
        url: `/client/renewals/${data.id}/payment`,
        method: "POST",
        data: data.body,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    verifyPayment: builder.mutation<void, any>({
      query: (data) => ({
        url: `/client/renewals/${data.id}/payment/verify`,
        method: "POST",
        data: data.body,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    telegramBot: builder.mutation<void, any>({
      query: (data) => ({
        url: "/profile/telegram-bot",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    checkVersion: builder.mutation<void, any>({
      query: (data) => ({
        url: "/webapp/check-version",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    changePhoneOtp: builder.mutation<any, any>({
      query: (data) => ({
        url: "/auth/change-phone/otp",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
    changePhone: builder.mutation<any, any>({
      query: (data) => ({
        url: "/auth/change-phone",
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["ilenkrad"],
    }),
  }),
});

export const {
  useAuthLoginMutation,
  useAuthSignupMutation,
  useAuthOtpSendMutation,
  useAuthOtpVerifyMutation,
  useAuthResetPasswordMutation,
  useProfileQuery,
  useLazyProfileQuery,
  useLazyProfileUpdateQuery,
  useTrackerIndexQuery,
  useInformationIndexQuery,
  useTrackerUnAssignMutation,
  useTrackerLocationLiveQuery,
  useSingleGeoLocationQuery,
  useTrackerAssignMutation,
  useTrackerBeforeUpdateQuery,
  useTrackerInformationQuery,
  useTrackerUpdateMutation,
  useServicesIndexQuery,
  useServicesAssignMutation,
  useServicesDeleteMutation,
  useServicesEditMutation,
  useLazyGeoLocationLiveQuery,
  useGeoLocationLiveQuery,
  useGeoFenceIndexQuery,
  useGeoFenceDeleteMutation,
  useGeoFenceAssignMutation,
  useGeoFenceShowQuery,
  useGeoFenceEditMutation,
  useChatIndexQuery,
  useHistoryReportMutation,
  useOnlineCommandMutation,
  useMessageChatIndexQuery,
  useSendMessageChatIndexMutation,
  useSendSyncOnlineCommandMutation,
  useOnlineCommandCheckMutation,
  usePreAssignTrackerMutation,
  useReadAllMessageMutation,
  useHistoryReportFleetMutation,
  useMessageChatBeforeMutation,
  useMessageChatFilterMutation,
  useFcmDeviceMutation,
  useFcmDeviceDeleteMutation,
  useSessionsListQuery,
  useSessionsDeleteAllMutation,
  useSessionsDeleteMutation,
  useSessionsTimeMutation,
  useMessageChatIndexMutateMutation,
  useProfileUpdatePasswordMutation,
  useReportLocationMutation,
  useRenewalsQuery,
  useRenewalPackagesQuery,
  useGetEligibleWarrantiesQuery,
  useCreateRenewalsMutation,
  useGetRenewalQuery,
  usePaymentMutation,
  useVerifyPaymentMutation,
  useTelegramBotMutation,
  useCheckVersionMutation,
  useChangePhoneOtpMutation,
  useChangePhoneMutation,
} = IlenkradApi;
